import React, { useEffect, useState } from 'react';
import { format, parseISO } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { DeviceApiKeyService } from '../../services/device-api-key.service';
import {
    Button,
    Box,
    Chip,
    Modal,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    IconButton,
    Tooltip,
    Checkbox,
    FormControlLabel,
    Alert,
} from '@mui/material';
import { ContentCopy } from '@mui/icons-material';
import ScrollableTable from '../common/ScrollableTable';
import { freeTierLimitMessage, isFreeTierLimitError } from '../../utils/planErrors';
import UpgradeNudge from '../common/UpgradeNudge';
import { toLocalIsoDate } from '../../utils/dateUtils';

interface DeviceApiKey {
    id: string;
    apiKeyPrefix: string;
    institutionId: string;
    deviceSn: string | null;
    description: string | null;
    isActive: boolean;
    createdAt: string;
    clockZone?: string | null;
    clockZoneFrom?: string | null;
}

/** S4-12: last sync of one attendance machine. */
interface DeviceSyncStatus {
    deviceSn: string;
    deviceApiKeyId: string | null;
    lastSyncAt: string;
    lastRecordCount: number;
    lastCreated: number;
    lastUpdated: number;
    lastSkipped: number;
    latestCheckTime: string | null;
    localClockSeenAt: string | null;
}

/** A machine with no successful sync for this long is flagged (B-08 default). */
const STALE_HOURS = 2;
const DHAKA = 'Asia/Dhaka';

const clockLabel = (key: DeviceApiKey) =>
    key.clockZone
        ? `${key.clockZone === DHAKA ? 'Bangladesh' : key.clockZone} time from ${key.clockZoneFrom ?? '-'}`
        : 'UTC (standard)';

// Audit Wave 2 / Lane 5 (H3) — the reveal-once shape. Only ever returned by generate/rotate,
// never by the list/get-by-id read paths, and only ever held transiently in `revealedKey` state
// below — never written back into the `keys` list.
interface DeviceApiKeyCreated extends DeviceApiKey {
    apiKey: string;
}

const DeviceApiKeyList = () => {
    const [keys, setKeys] = useState<DeviceApiKey[]>([]);
    const [loading, setLoading] = useState(true);

    // Generate dialog
    const [generateOpen, setGenerateOpen] = useState(false);
    const [description, setDescription] = useState('');
    const [deviceSn, setDeviceSn] = useState('');
    const [generating, setGenerating] = useState(false);
    // Null hides the nudge; a string is the (server or fallback) copy to show.
    const [capNudgeMessage, setCapNudgeMessage] = useState<string | null>(null);

    // Newly generated/rotated key highlight. `revealedKeyOwnerId` tracks WHICH row the plaintext
    // belongs to — the delete-confirm dismissal below compares on id, not on the plaintext itself
    // (the list rows never carry the plaintext to compare against post-H3).
    const [revealedKey, setRevealedKey] = useState<string | null>(null);
    const [revealedKeyOwnerId, setRevealedKeyOwnerId] = useState<string | null>(null);

    // Rotate confirmation
    const [rotateTarget, setRotateTarget] = useState<DeviceApiKey | null>(null);
    const [rotating, setRotating] = useState(false);

    // Delete confirmation
    const [deleteTarget, setDeleteTarget] = useState<DeviceApiKey | null>(null);
    const [deleting, setDeleting] = useState(false);

    // S4-12: last sync per machine + the per-key clock setting
    const [syncStatus, setSyncStatus] = useState<DeviceSyncStatus[]>([]);
    const [clockTarget, setClockTarget] = useState<DeviceApiKey | null>(null);
    const [clockLocal, setClockLocal] = useState(false);
    const [clockFrom, setClockFrom] = useState<string>(toLocalIsoDate());
    const [savingClock, setSavingClock] = useState(false);

    const navigate = useNavigate();
    const { isAuthenticated } = useAuth();
    const toast = useToast();

    useEffect(() => {
        if (!isAuthenticated) {
            navigate('/login');
            return;
        }
        fetchKeys();
    }, [isAuthenticated, navigate]);

    const fetchKeys = async () => {
        try {
            setLoading(true);
            const data = await DeviceApiKeyService.getByInstitution();
            setKeys(data);
            try {
                setSyncStatus((await DeviceApiKeyService.getSyncStatus()) ?? []);
            } catch {
                setSyncStatus([]);
            }
        } catch (err: any) {
            toast.showApiError(err, 'Failed to load API keys');
        } finally {
            setLoading(false);
        }
    };

    const handleGenerate = async () => {
        if (!description.trim()) return;
        setCapNudgeMessage(null);
        try {
            setGenerating(true);
            const created: DeviceApiKeyCreated = await DeviceApiKeyService.generate(description.trim(), deviceSn.trim());
            setKeys(prev => [created, ...prev]);
            setRevealedKey(created.apiKey);
            setRevealedKeyOwnerId(created.id);
            setGenerateOpen(false);
            setDescription('');
            setDeviceSn('');
            toast.showSuccess('API key generated');
        } catch (err: any) {
            if (isFreeTierLimitError(err)) {
                // FREE refuses device-key generation outright — there's no numeric limit to cite,
                // so prefer the server's own wording and fall back to a generic line.
                setCapNudgeMessage(freeTierLimitMessage(err) ?? 'Device sync requires a paid plan.');
            } else {
                toast.showApiError(err, 'Failed to generate API key');
            }
        } finally {
            setGenerating(false);
        }
    };

    const handleRotate = async () => {
        if (!rotateTarget) return;
        try {
            setRotating(true);
            const updated: DeviceApiKeyCreated = await DeviceApiKeyService.rotate(rotateTarget.id);
            setKeys(prev => prev.map(k => k.id === updated.id ? updated : k));
            setRevealedKey(updated.apiKey);
            setRevealedKeyOwnerId(updated.id);
            setRotateTarget(null);
            toast.showSuccess('API key rotated');
        } catch (err: any) {
            toast.showApiError(err, 'Failed to rotate API key');
        } finally {
            setRotating(false);
        }
    };

    const handleToggleActive = async (key: DeviceApiKey) => {
        try {
            const updated: DeviceApiKey = await DeviceApiKeyService.toggleActive(key.id);
            setKeys(prev => prev.map(k => k.id === updated.id ? updated : k));
            toast.showSuccess(updated.isActive ? 'Key activated' : 'Key deactivated');
        } catch (err: any) {
            toast.showApiError(err, 'Failed to toggle key status');
        }
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;
        try {
            setDeleting(true);
            await DeviceApiKeyService.deleteKey(deleteTarget.id);
            setKeys(prev => prev.filter(k => k.id !== deleteTarget.id));
            if (revealedKeyOwnerId === deleteTarget.id) {
                setRevealedKey(null);
                setRevealedKeyOwnerId(null);
            }
            setDeleteTarget(null);
            toast.showSuccess('API key deleted');
        } catch (err: any) {
            toast.showApiError(err, 'Failed to delete API key');
        } finally {
            setDeleting(false);
        }
    };

    const openClock = (key: DeviceApiKey) => {
        setClockTarget(key);
        setClockLocal(!!key.clockZone);
        setClockFrom(key.clockZoneFrom ?? toLocalIsoDate());
    };

    const handleSaveClock = async () => {
        if (!clockTarget) return;
        try {
            setSavingClock(true);
            const updated: DeviceApiKey = clockLocal
                ? await DeviceApiKeyService.setClockZone(clockTarget.id, DHAKA, clockFrom || null)
                : await DeviceApiKeyService.setClockZone(clockTarget.id, '', null);
            setKeys(prev => prev.map(k => k.id === updated.id ? updated : k));
            setClockTarget(null);
            toast.showSuccess('Clock setting saved');
        } catch (err: any) {
            toast.showApiError(err, 'Failed to save the clock setting');
        } finally {
            setSavingClock(false);
        }
    };

    const hoursSince = (iso: string) => (Date.now() - new Date(iso).getTime()) / 3600_000;

    const copyToClipboard = (value: string) => {
        navigator.clipboard.writeText(value).then(() => {
            toast.showSuccess('Copied to clipboard');
        });
    };

    const formatDate = (dateStr: string) => {
        if (!dateStr) return '-';
        return format(parseISO(dateStr), 'dd-MM-yyyy');
    };

    if (loading && keys.length === 0) return <div>Loading...</div>;

    return (
        <div style={{ padding: '20px' }} data-help-page="deviceKey.list.page">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' , flexWrap: 'wrap', gap: '8px' }}>
                <h2>Device API Keys</h2>
                <Button data-help="deviceKey.list.generate" variant="contained" color="primary" onClick={() => setGenerateOpen(true)}>
                    Generate New Key
                </Button>
            </div>

            {revealedKey && (
                <Box data-help-section="deviceKey.list.reveal" sx={{
                    mb: 3, p: 2, backgroundColor: '#e8f5e9', borderRadius: 2,
                    border: '1px solid #a5d6a7', display: 'flex', alignItems: 'center', gap: 1
                }}>
                    <Box sx={{ flex: 1 }}>
                        <strong>New Key (copy it now):</strong>
                        <Box component="code" sx={{ display: 'block', mt: 0.5, wordBreak: 'break-all', fontFamily: 'monospace' }}>
                            {revealedKey}
                        </Box>
                    </Box>
                    <Tooltip title="Copy to clipboard">
                        <IconButton data-help="deviceKey.list.copy" onClick={() => copyToClipboard(revealedKey)} size="small" aria-label="Copy to clipboard">
                            <ContentCopy fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Button data-help="deviceKey.list.dismiss" size="small" onClick={() => setRevealedKey(null)}>Dismiss</Button>
                </Box>
            )}

            <ScrollableTable>
            <table data-help-section="deviceKey.list.table" style={{ width: '100%', borderCollapse: 'collapse', minWidth: 640 }}>
                <thead>
                    <tr>
                        <th style={tableHeaderStyle} data-help="deviceKey.list.col.description">Description</th>
                        <th style={tableHeaderStyle} data-help="deviceKey.list.col.deviceSn">Device SN</th>
                        <th style={tableHeaderStyle} data-help="deviceKey.list.col.status">Status</th>
                        <th style={tableHeaderStyle} data-help="deviceKey.list.col.key">Key</th>
                        <th style={tableHeaderStyle} data-help="deviceKey.list.col.clock">Clock</th>
                        <th style={tableHeaderStyle} data-help="deviceKey.list.col.created">Created</th>
                        <th style={tableHeaderStyle} data-help="deviceKey.list.col.actions">Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {keys.map(key => (
                        <tr key={key.id}>
                            <td style={tableCellStyle}>{key.description || '-'}</td>
                            <td style={tableCellStyle}>{key.deviceSn || <em style={{ color: '#999' }}>Any</em>}</td>
                            <td style={tableCellStyle}>
                                <Chip
                                    label={key.isActive ? 'Active' : 'Inactive'}
                                    color={key.isActive ? 'success' : 'default'}
                                    size="small"
                                />
                            </td>
                            <td style={tableCellStyle}>
                                {/* H3: the plaintext key is never served here — only the display
                                    prefix. The full key is shown exactly once, in the reveal
                                    banner above, right after generate/rotate. */}
                                <code style={{ fontSize: '0.8rem', wordBreak: 'break-all' }}>{key.apiKeyPrefix}…</code>
                            </td>
                            <td style={tableCellStyle}>{clockLabel(key)}</td>
                            <td style={tableCellStyle}>{formatDate(key.createdAt)}</td>
                            <td style={tableCellStyle}>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Button
                                        data-help="deviceKey.list.action.rotate"
                                        size="small"
                                        variant="outlined"
                                        color="warning"
                                        onClick={() => setRotateTarget(key)}
                                    >
                                        Rotate
                                    </Button>
                                    <Button
                                        data-help="deviceKey.list.action.clock"
                                        size="small"
                                        variant="outlined"
                                        onClick={() => openClock(key)}
                                    >
                                        Clock
                                    </Button>
                                    <Button
                                        data-help="deviceKey.list.action.toggle"
                                        size="small"
                                        variant="outlined"
                                        color={key.isActive ? 'secondary' : 'success'}
                                        onClick={() => handleToggleActive(key)}
                                    >
                                        {key.isActive ? 'Deactivate' : 'Activate'}
                                    </Button>
                                    <Button
                                        data-help="deviceKey.list.action.delete"
                                        size="small"
                                        variant="outlined"
                                        color="error"
                                        onClick={() => setDeleteTarget(key)}
                                    >
                                        Delete
                                    </Button>
                                </Box>
                            </td>
                        </tr>
                    ))}
                    {keys.length === 0 && (
                        <tr>
                            <td style={tableCellStyle} colSpan={7}>No API keys found. Generate one to get started.</td>
                        </tr>
                    )}
                </tbody>
            </table>
            </ScrollableTable>

            <h3 style={{ marginTop: 28 }}>Machines — last sync</h3>
            <ScrollableTable>
            <table data-testid="device-sync-status" data-help-section="deviceKey.sync.table" style={{ width: '100%', borderCollapse: 'collapse', minWidth: 640 }}>
                <thead>
                    <tr>
                        <th style={tableHeaderStyle}>Device SN</th>
                        <th style={tableHeaderStyle}>Last sync</th>
                        <th style={tableHeaderStyle}>Last batch</th>
                        <th style={tableHeaderStyle}>Newest punch sent</th>
                        <th style={tableHeaderStyle}>Health</th>
                    </tr>
                </thead>
                <tbody>
                    {syncStatus.map(s => {
                        const hours = hoursSince(s.lastSyncAt);
                        const key = keys.find(k => k.id === s.deviceApiKeyId);
                        const localClockUnhandled = !!s.localClockSeenAt && !key?.clockZone;
                        return (
                            <tr key={s.deviceSn}>
                                <td style={tableCellStyle}>{s.deviceSn}</td>
                                <td style={tableCellStyle}>{format(parseISO(s.lastSyncAt), 'dd-MM-yyyy HH:mm')}</td>
                                <td style={tableCellStyle}>{s.lastRecordCount} punches ({s.lastCreated} new, {s.lastUpdated} updated, {s.lastSkipped} skipped)</td>
                                <td style={tableCellStyle}>{s.latestCheckTime ?? '-'}</td>
                                <td style={tableCellStyle}>
                                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                        {hours > STALE_HOURS
                                            ? <Chip size="small" color="error" label={`No sync for ${Math.floor(hours)} h`} />
                                            : <Chip size="small" color="success" label="Syncing" />}
                                        {localClockUnhandled && (
                                            <Tooltip title="This PC sends Bangladesh local time, so its punches are stored 6 hours late. Open Clock on its key and tick the local-time box.">
                                                <Chip size="small" color="warning" label="PC sends local time" />
                                            </Tooltip>
                                        )}
                                    </Box>
                                </td>
                            </tr>
                        );
                    })}
                    {syncStatus.length === 0 && (
                        <tr>
                            <td style={tableCellStyle} colSpan={5}>No machine has synced yet. Rows appear after the first punch upload.</td>
                        </tr>
                    )}
                </tbody>
            </table>
            </ScrollableTable>

            {/* S4-12 clock setting */}
            <Dialog open={!!clockTarget} onClose={() => !savingClock && setClockTarget(null)}>
                <DialogTitle>Machine clock — {clockTarget?.description || 'key'}</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        The current sync program sends UTC, which is right for most machines. Tick the box only when the
                        Machines table says &quot;PC sends local time&quot; for this key. Punches before the start date are not changed.
                    </DialogContentText>
                    <FormControlLabel
                        data-help="deviceKey.clock.local"
                        control={<Checkbox checked={clockLocal} onChange={e => setClockLocal(e.target.checked)} />}
                        label="This PC sends Bangladesh local time"
                    />
                    {clockLocal && (
                        <TextField
                            data-help="deviceKey.clock.from"
                            label="Read as local time from" type="date" size="small" value={clockFrom}
                            onChange={e => setClockFrom(e.target.value)}
                            slotProps={{ inputLabel: { shrink: true } }}
                            sx={{ mt: 2, display: 'block' }}
                        />
                    )}
                    {clockLocal && <Alert severity="info" sx={{ mt: 2 }}>A 22:00 punch from this PC will be stored as 22:00 Bangladesh time.</Alert>}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setClockTarget(null)} disabled={savingClock}>Cancel</Button>
                    <Button onClick={handleSaveClock} variant="contained" disabled={savingClock || (clockLocal && !clockFrom)}>Save</Button>
                </DialogActions>
            </Dialog>

            {/* Generate Dialog */}
            <Modal open={generateOpen} onClose={() => { setGenerateOpen(false); setDescription(''); setDeviceSn(''); setCapNudgeMessage(null); }}>
                <Box sx={{
                    position: 'absolute', top: '50%', left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 420, bgcolor: 'background.paper', boxShadow: 24, p: 4, borderRadius: 2
                }}>
                    <h3 style={{ marginTop: 0 }}>Generate New API Key</h3>
                    {capNudgeMessage && (
                        <UpgradeNudge sx={{ mb: 2 }} message={capNudgeMessage} />
                    )}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <TextField
                            data-help="deviceKey.list.formDescription"
                            label="Description"
                            value={description}
                            onChange={e => setDescription(e.target.value)}
                            fullWidth
                            required
                            placeholder="e.g. Main Office Device"
                        />
                        <TextField
                            data-help="deviceKey.list.formDeviceSn"
                            label="Device Serial Number (optional)"
                            value={deviceSn}
                            onChange={e => setDeviceSn(e.target.value)}
                            fullWidth
                            placeholder="Leave blank to allow any device"
                            helperText="If set, this key will only work for that specific device"
                        />
                    </Box>
                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                        <Button onClick={() => { setGenerateOpen(false); setDescription(''); setDeviceSn(''); setCapNudgeMessage(null); }} variant="outlined">
                            Cancel
                        </Button>
                        <Button
                            onClick={handleGenerate}
                            variant="contained"
                            color="primary"
                            disabled={!description.trim() || generating}
                        >
                            {generating ? 'Generating...' : 'Generate'}
                        </Button>
                    </Box>
                </Box>
            </Modal>

            {/* Rotate Confirmation */}
            <Dialog open={!!rotateTarget} onClose={() => setRotateTarget(null)}>
                <DialogTitle>Rotate API Key</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        This will invalidate the current key for <strong>{rotateTarget?.description || 'this entry'}</strong> and generate a new one.
                        Any system using the old key will stop working until updated.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRotateTarget(null)}>Cancel</Button>
                    <Button onClick={handleRotate} color="warning" variant="contained" disabled={rotating}>
                        {rotating ? 'Rotating...' : 'Rotate Key'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation */}
            <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
                <DialogTitle>Delete API Key</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete the key for <strong>{deleteTarget?.description || 'this entry'}</strong>?
                        This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
                    <Button onClick={handleDelete} color="error" variant="contained" disabled={deleting}>
                        {deleting ? 'Deleting...' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

const tableHeaderStyle = {
    backgroundColor: '#f4f4f4',
    padding: '12px',
    textAlign: 'left' as const,
    borderBottom: '2px solid #ddd'
};

const tableCellStyle = {
    padding: '12px',
    borderBottom: '1px solid #ddd'
};

export default DeviceApiKeyList;
